import type { CSSObject } from 'styled-components';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { fadeIn } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import {
	DS_AI_MOTION,
	dsAiFocusRingStyles,
	dsAiMoveMs,
} from '../../../../theme/dealstackAi.tokens';

const { colors } = defaultTheme.v3;

/** Figma's 8/8 dashed outline; CSS `dashed` draws much shorter dashes. */
const dashedOutline = (color: string) =>
	`url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='${encodeURIComponent(
		color
	)}' stroke-width='2' stroke-dasharray='8%2c 8'/%3e%3c/svg%3e")`;

/** Fades a part of the panel in: the panel body, a card's pill row and its detail lines. */
export const UPLOAD_FADE_IN_STYLES = {
	animation: `${fadeIn} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing} both`,
} satisfies CSSObject;

/** The dropzone itself; `[data-upload-icon]` is the tile inside it, which lifts while a drop is accepted. */
export const DROPZONE_STYLES = {
	root: {
		'&&': {
			display: 'flex',
			alignItems: 'center',
			minHeight: '11.75rem',
			padding: 16,
			border: 'none',
			borderRadius: 8,
			backgroundColor: colors.white[0],
			backgroundImage: dashedOutline(colors.gray[6]),
			cursor: 'pointer',
			transition: `background-color ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing}, transform ${dsAiMoveMs(DS_AI_MOTION.baseMs)} ${DS_AI_MOTION.easing}`,
			// && outranks the .mantine-focus-auto ring.
			...dsAiFocusRingStyles(2),
		},
		'&&:hover': { backgroundColor: colors.gray[0] },
		'&&[data-accept]': {
			backgroundColor: colors.blue[0],
			backgroundImage: dashedOutline(colors.blue[6]),
			transform: 'scale(1.005)',
			'& [data-upload-icon]': {
				background: colors.white[0],
				color: colors.blue[6],
				transform: 'translateY(-2px)',
			},
		},
		'&&[data-reject]': {
			backgroundColor: colors.red[0],
			backgroundImage: dashedOutline(colors.red[6]),
		},
	},
	inner: { width: '100%' },
} satisfies Record<string, CSSObject>;

/** Delay of a card's entrance, capped so a large drop's last cards do not wait (the design's `.stagger`). */
export const cardStaggerDelay = (staggerIndex: number) =>
	dsAiMoveMs(Math.min(staggerIndex, 30) * DS_AI_MOTION.cardStaggerMs);
